/**
 * personalization-sync.ts — hybrid localStorage→DB mirror for signed-in users.
 *
 * Anonymous visitors are untouched: AppState (localStorage) stays the single
 * source of truth. When `window.__TN_AUTH__.isAuthenticated` is true this
 * module:
 *   1. On first authenticated load, POSTs the local AppState snapshot
 *      (favorites/recent/pinned/collections) to `/api/user/personalization/sync`
 *      so device-local history merges into the account (idempotent server-side).
 *   2. Exposes fire-and-forget helpers the UI calls alongside AppState writes,
 *      so localStorage remains the instant write-through cache while the server
 *      catches up in the background. Network failures never block the UI.
 *
 * No third-party requests; all POSTs are same-origin with the anti-forgery
 * header token emitted in _SharedShellLayoutBase.cshtml.
 */
import { AppState } from "./state.js";
import { apiHeaders } from "../localization/locale-runtime.js";
const API_BASE = "/api/user/personalization";
const EVENTS_URL = "/api/events/tool";
function isAuthenticated() {
    return window.__TN_AUTH__?.isAuthenticated === true;
}
/**
 * Fire-and-forget behavioural event beacon. Unlike the personalization API this
 * is anonymous-tolerant (the endpoint is [AllowAnonymous] + CSRF-exempt and keys
 * anon visitors by a hashed first-party cookie server-side), so it runs for every
 * visitor. Writes to UserToolEvents, a different table from personalization
 * history — no double-count with recordUsage. Never throws.
 */
function recordEvent(toolSlug, eventType) {
    if (!toolSlug || !eventType)
        return;
    try {
        void fetch(EVENTS_URL, {
            method: "POST",
            credentials: "same-origin",
            keepalive: true,
            headers: apiHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({ toolSlug, eventType }),
        }).catch(() => { });
    }
    catch {
        /* telemetry must never break the UI */
    }
}
function antiForgeryToken() {
    const meta = document.querySelector('meta[name="request-verification-token"]');
    return meta?.content ?? null;
}
/**
 * Same-origin JSON POST/DELETE with anti-forgery header. Fire-and-forget:
 * resolves to the parsed body on success, or null on any failure (the UI has
 * already updated localStorage, so a failed sync is non-fatal).
 */
async function apiSend(path, method, body) {
    const token = antiForgeryToken();
    if (token === null) {
        console.error("[personalization-sync] Anti-forgery token not found");
        return null;
    }
    // Check if online
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        console.warn("[personalization-sync] Offline, skipping API call");
        return null;
    }
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        const init = {
            method,
            credentials: "same-origin",
            headers: apiHeaders({
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            }),
            signal: controller.signal,
        };
        if (body !== undefined) {
            init.body = JSON.stringify(body);
        }
        const res = await fetch(`${API_BASE}${path}`, init);
        clearTimeout(timeoutId);
        // Log HTTP errors
        if (!res.ok) {
            console.error(`[personalization-sync] API error: ${res.status} ${res.statusText} for ${method} ${path}`);
            // Handle specific status codes
            if (res.status === 401) {
                console.warn("[personalization-sync] Unauthorized - user may need to re-login");
            }
            else if (res.status === 403) {
                console.warn("[personalization-sync] Forbidden - insufficient permissions");
            }
            else if (res.status >= 500) {
                console.error("[personalization-sync] Server error - backend issue");
            }
            return null;
        }
        // Check Content-Type before parsing
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.error(`[personalization-sync] Expected JSON but got ${contentType}`);
            return null;
        }
        // Parse JSON with error handling
        try {
            return (await res.json());
        }
        catch (parseError) {
            console.error("[personalization-sync] JSON parse error:", parseError);
            return null;
        }
    }
    catch (error) {
        if (error instanceof Error) {
            if (error.name === "AbortError") {
                console.error("[personalization-sync] Request timeout");
            }
            else {
                console.error("[personalization-sync] Network error:", error.message);
            }
        }
        return null;
    }
}
async function apiGet(path) {
    // Check if online
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        console.warn("[personalization-sync] Offline, skipping API call");
        return null;
    }
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        const res = await fetch(`${API_BASE}${path}`, {
            method: "GET",
            credentials: "same-origin",
            headers: apiHeaders({ Accept: "application/json" }),
            signal: controller.signal,
        });
        clearTimeout(timeoutId);
        // Log HTTP errors
        if (!res.ok) {
            console.error(`[personalization-sync] API error: ${res.status} ${res.statusText} for GET ${path}`);
            if (res.status === 401) {
                console.warn("[personalization-sync] Unauthorized - user may need to re-login");
            }
            else if (res.status === 403) {
                console.warn("[personalization-sync] Forbidden - insufficient permissions");
            }
            else if (res.status >= 500) {
                console.error("[personalization-sync] Server error - backend issue");
            }
            return null;
        }
        // Check Content-Type before parsing
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.error(`[personalization-sync] Expected JSON but got ${contentType}`);
            return null;
        }
        // Parse JSON with error handling
        try {
            return (await res.json());
        }
        catch (parseError) {
            console.error("[personalization-sync] JSON parse error:", parseError);
            return null;
        }
    }
    catch (error) {
        if (error instanceof Error) {
            if (error.name === "AbortError") {
                console.error("[personalization-sync] Request timeout");
            }
            else {
                console.error("[personalization-sync] Network error:", error.message);
            }
        }
        return null;
    }
}
/** Build the merge payload from the current local AppState snapshot. */
function buildSyncPayload() {
    const favorites = AppState.favoriteTools.get().map((entry) => entry.slug);
    const pinnedTools = AppState.pinnedTools.get();
    const history = AppState.toolHistory.get().map((e) => ({
        toolSlug: e.slug,
        action: e.action ?? null,
        createdAt: e.createdAt ?? null,
    }));
    const collections = AppState.toolCollections.get();
    return { favorites, pinnedTools, history, collections };
}
/**
 * First-login merge. Guarded by AppState so we only push the local snapshot
 * once per browser; subsequent loads rely on live mutation mirroring.
 */
async function syncOnFirstLogin() {
    if (window.__TN_PERSONALIZATION_SYNCED__ === true)
        return;
    window.__TN_PERSONALIZATION_SYNCED__ = true;
    if (AppState.personalizationSync.hasSynced())
        return;
    const payload = buildSyncPayload();
    const hasLocalData = payload.favorites.length > 0 ||
        payload.pinnedTools.length > 0 ||
        payload.history.length > 0 ||
        Object.keys(payload.collections).length > 0;
    // Even with no local data we set the flag to avoid re-checking every load.
    if (hasLocalData) {
        await apiSend("/sync", "POST", payload);
    }
    AppState.personalizationSync.markSynced();
}
export const PersonalizationSync = Object.freeze({
    /** True when the current visitor is signed in (server-mirrored). */
    get enabled() {
        return isAuthenticated();
    },
    /** Mirror a favorite toggle to the account (fire-and-forget). */
    toggleFavorite(toolSlug) {
        if (!isAuthenticated())
            return;
        void apiSend("/favorite/toggle", "POST", { toolSlug });
    },
    /** Mirror a tool run to the account history (fire-and-forget). */
    recordUsage(toolSlug, action) {
        // Behavioural event log: bootstrap_complete = the tool page loaded and the
        // runtime is ready, i.e. a "view". Fires for ALL visitors (anon + authed) via
        // the anonymous-tolerant beacon. The authed-only personalization history mirror
        // below is an additional write to a separate table.
        recordEvent(toolSlug, "view");
        if (!isAuthenticated())
            return;
        void apiSend("/usage", "POST", { toolSlug, action: action ?? null });
    },
    /** Mirror a pin to the account shortcuts (fire-and-forget). */
    addShortcut(toolSlug, label) {
        if (!isAuthenticated())
            return Promise.resolve(null);
        return apiSend("/shortcut", "POST", { toolSlug, label: label ?? null });
    },
    /** Remove a shortcut by ID. */
    removeShortcut(shortcutId) {
        if (!isAuthenticated())
            return Promise.resolve(false);
        return apiSend(`/shortcut/${shortcutId}`, "DELETE").then((result) => result !== null);
    },
    /** Fetch server-side per-tool state (favorite + shortcut) for hydration. */
    fetchState(slug) {
        if (!isAuthenticated())
            return Promise.resolve(null);
        return apiGet(`/state?slug=${encodeURIComponent(slug)}`);
    },
    /** Fetch the server recent list (authed) for the history rail. */
    fetchRecent(take = 20) {
        if (!isAuthenticated())
            return Promise.resolve(null);
        return apiGet(`/recent?take=${take}`);
    },
    /** List the account's collections (authed) for the add-to-collection picker. */
    listCollections() {
        if (!isAuthenticated())
            return Promise.resolve(null);
        return apiGet("/collections");
    },
    /** Create a collection; returns its id or null on failure. */
    createCollection(name) {
        if (!isAuthenticated())
            return Promise.resolve(null);
        return apiSend("/collection", "POST", { name });
    },
    /** Add a tool to a collection (fire-and-forget-ish); resolves true on success. */
    addToCollection(collectionId, toolSlug) {
        if (!isAuthenticated())
            return Promise.resolve(false);
        return apiSend(`/collection/${collectionId}/add`, "POST", { toolSlug }).then((r) => r !== null);
    },
});
if (typeof window !== "undefined") {
    window.PersonalizationSync =
        PersonalizationSync;
    if (isAuthenticated()) {
        // Defer the first-login merge to idle so it never competes with first paint.
        const run = () => {
            void syncOnFirstLogin();
        };
        if (document.readyState === "complete") {
            window.setTimeout(run, 0);
        }
        else {
            window.addEventListener("load", () => window.setTimeout(run, 0), { once: true });
        }
    }
}
//# sourceMappingURL=personalization-sync.js.map